/** All tasks, and — with `carriedOnly` — Carried forward.
 *
 *  This page used to ask for `/tasks` with no date at all, so it returned the firm's
 *  entire history on one screen with no way to tell which day a row belonged to. It
 *  now opens on a window — the last week, plus everything scheduled ahead — and
 *  groups by date.
 *
 *  **The default must not lie.** A window silently hides real data, so the page always
 *  says which window it is showing, "All time" is one tap away, and an empty result
 *  says *why* it is empty — a task that exists just outside the window must never
 *  produce a flat "No tasks match", which is false and gets reported as a bug.
 *
 *  Filters live in the URL so Reports can link straight to the tasks behind a figure,
 *  and so a refresh does not lose them. `Approvals` already reads `useSearchParams`
 *  for its `?ref=` deep link, so this is an existing pattern rather than a new one.
 */
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, type Task } from '@/api/client'
import { TaskList } from '@/components/TaskList'
import { DropSelected } from '@/components/DropSelected'
import { useSelection } from '@/components/Selection'
import { useChannel } from '@/lib/channel'
import { type GroupBy } from '@/components/TaskTable'
import { TaskDrawer } from '@/components/TaskDrawer'
import { AddTaskDrawer } from '@/components/AddTaskDrawer'
import { useAuth } from '@/lib/auth'
import {
  Chips, Empty, ErrorNote, Spinner, addDays, fmtDate, monthStartISO,
  todayISO, toggleIn,
} from '@/components/ui'

const STATUSES = ['OPEN', 'PENDING_APPROVAL', 'REWORK', 'COMPLETED', 'CANCELLED']

// On the firm's calendar, not the browser's - see `addDays` in `ui`.
const daysAgo = (n: number) => addDays(todayISO(), -n)
const monthStart = () => monthStartISO()

/** `range` is what lives in the URL; from/to are derived from it, except for
 *  `custom` where they are the URL's own. One parameter rather than two makes a link
 *  from Reports readable, and makes "all time" a state rather than two empty inputs
 *  that could equally mean "not chosen yet".
 *
 *  **The default is open-ended forwards, and that is the point.** It used to be
 *  `today-6 → today`, which meant writing tomorrow's list — the thing this app most
 *  encourages, and offers a button for — produced "Nothing in last 7 days" on the
 *  screen you would naturally check afterwards. Bounding the future at +7 would only
 *  move that cliff to whoever plans a fortnight out, so there is no upper bound at
 *  all: work you have scheduled can never fall out of the default view.
 *
 *  `sentence` is how the range reads mid-prose. It is written per range rather than
 *  derived, because "showing last 7 days" and "showing the last 7 days and anything
 *  scheduled ahead" are not the same claim.
 */
const RANGES: Record<string, {
  label: string; from: () => string; to: () => string; sentence: string
}> = {
  recent: {
    label: 'Recent & upcoming',
    from: () => daysAgo(6),
    to: () => '',
    sentence: 'the last 7 days and anything scheduled ahead',
  },
  '7d': {
    label: 'Last 7 days',
    from: () => daysAgo(6), to: todayISO, sentence: 'the last 7 days',
  },
  '30d': {
    label: 'Last 30 days',
    from: () => daysAgo(29), to: todayISO, sentence: 'the last 30 days',
  },
  month: {
    label: 'This month',
    from: monthStart, to: todayISO, sentence: 'this month so far',
  },
  all: {
    label: 'All time', from: () => '', to: () => '', sentence: 'everything',
  },
}

export function AllTasks({ carriedOnly = false }: { carriedOnly?: boolean }) {
  const { can } = useAuth()
  // Capability only - `can('cancel')` answers "would this person ever be allowed
  // to?", published by `/auth/me`. Every task is re-checked server-side inside the
  // loop, and a manager's refusals come back in the report rather than being
  // guessed at here.
  const dropping = can('cancel')
  const picked = useSelection()
  const { label: channelName } = useChannel()
  const [params, setParams] = useSearchParams()
  const [open, setOpen] = useState<Task | null>(null)
  // Every filter on this page lives in the URL, so one dependency covers all of
  // them. A tick that outlived a filter change would let somebody drop rows the
  // screen had stopped showing.
  useEffect(() => { picked.clear() }, [params.toString(), picked.clear])
  const [adding, setAdding] = useState(false)

  const range = params.get('range') ?? 'recent'
  const preset = RANGES[range]
  const from = range === 'custom' ? (params.get('from') ?? '') : (preset?.from() ?? '')
  const to = range === 'custom' ? (params.get('to') ?? '') : (preset?.to() ?? '')
  const q = params.get('q') ?? ''
  // In the URL like every filter on this page, so a link from Reports and a refresh
  // both keep it. Deliberately NOT in `query` below - it groups rows the browser
  // already has, and the query string is the react-query key, so sending it would
  // refetch the same rows on every toggle.
  const groupBy: GroupBy = params.get('group') === 'person' ? 'person' : 'company'

  // `has` rather than `get`: an explicitly empty status means "any status", which is
  // a different thing from not having chosen one. Carried forward opens on unfinished
  // work only - the live backlog rather than a record of everything that ever moved.
  const statusRaw = params.has('status')
    ? params.get('status')!
    : (carriedOnly ? 'OPEN,REWORK' : '')
  const statuses = statusRaw.split(',').filter(Boolean)

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k)
      else next.set(k, v)
    }
    setParams(next, { replace: true })
  }

  const query = new URLSearchParams()
  if (q) query.set('q', q)
  if (from) query.set('date_from', from)
  if (to) query.set('date_to', to)
  if (carriedOnly) query.set('carried_forward', 'true')
  const responsible = params.get('responsible')
  const approver = params.get('approver')
  const company = params.get('company')
  if (responsible) query.set('responsible_id', responsible)
    // Reports has always linked here with `approver`, and nothing read it - so
    // that drill-in silently showed every task instead of the approver's.
    if (approver) query.set('approver_id', approver)
  if (company) query.set('company_id', company)
  for (const s of statuses) query.append('status', s)

  const tasks = useQuery({
    queryKey: ['tasks', query.toString()],
    queryFn: () => api.get<Task[]>(`/tasks?${query.toString()}`),
  })

  // The same filters, in the names that screen reads. `from`/`to` become
  // `date_from`/`date_to`, the three id filters gain their `_id`, and the statuses
  // stop being comma-joined and start being repeated - which is what FastAPI reads a
  // list from. Carried forward is a route here and a filter there.
  const exportHref = () => {
    const p = new URLSearchParams()
    if (from) p.set('date_from', from)
    if (to) p.set('date_to', to)
    if (q) p.set('q', q)
    if (responsible) p.set('responsible_id', responsible)
    if (approver) p.set('approver_id', approver)
    if (company) p.set('company_id', company)
    if (carriedOnly) p.set('carried_forward', 'true')
    for (const s of statuses) p.append('status', s)
    // Arriving grouped the way this page is grouped, so the first thing somebody
    // sees is the list they just left rather than an ungrouped one.
    p.set('group_by', groupBy === 'person' ? 'responsible' : 'company')
    return `/explore?${p.toString()}`
  }

  const rows = tasks.data ?? []
  // Tasks with more than one doer. Only used to caption the person grouping, which
  // lists such a task under each of them - see the footer below.
  const shared = rows.filter(
    (t) => (t.people ?? []).filter((p) => p.kind === 'RESPONSIBLE').length > 1).length
  const windowed = !!(from || to)
  const filtered = !!(q || statuses.length || responsible || approver
                      || company)

  // Two labels, because a preset name reads correctly mid-sentence in lower case
  // ("showing last 7 days") and a formatted date does not ("nothing in wed, 01 jan").
  const custom = range === 'custom'
  const label = custom
    ? `${from ? fmtDate(from) : 'the beginning'} → ${to ? fmtDate(to) : 'no end date'}`
    : (preset?.label ?? 'Recent & upcoming')
  const inSentence = custom ? label : (preset?.sentence ?? label.toLowerCase())

  return (
    <>
      <div className="filter-bar mb-3">
        <input className="field w-full sm:w-auto sm:flex-1 sm:max-w-xs"
               placeholder="Search tasks, companies or people"
               value={q} onChange={(e) => set({ q: e.target.value || null })} />
        {/* Several statuses, not one. The URL and the API have always taken a list -
            this page even reads one back out of `params` - so the `<select>` was the
            only thing insisting on a single value, and "open or in rework" is the
            obvious thing to want from a task list. */}
        {/* No `anyLabel`: with nothing ticked this said "All statuses" in grey
            UNDER the buttons, which made the chip block taller than the chips and
            left them riding high against the search box beside it. `Chips` now shows
            that hint on its label line, and this call has no label. */}
        <Chips selected={statuses}
               onToggle={(v) => {
                 const next = toggleIn(statuses, v)
                 set({ status: next.length ? next.join(',') : '' })
               }}
               options={STATUSES.map((s) => (
                 { value: s, label: s.replace('_', ' ').toLowerCase() }))} />
        <span className="hidden lg:block flex-1" />
        {/* Group by. The dates stay the outer heading - this subdivides each day, so
            "what is on this client" and "what is Shyam carrying" are answerable
            without losing which day a row belongs to.

            Hidden when there is nothing to group, matching Today: a control over
            nothing invites a click that changes nothing. */}
        {rows.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-[11.5px] text-ink-3">Group by</span>
            <div className="inline-flex rounded-md border border-rule overflow-hidden">
              {(['company', 'person'] as const).map((g) => (
                <button key={g} type="button" onClick={() => set({ group: g })}
                        aria-pressed={groupBy === g}
                        className={`px-2.5 h-11 sm:h-8 text-[12px] capitalize transition-colors ${
                          groupBy === g ? 'bg-primary text-primary-on font-medium'
                                        : 'bg-surface text-ink-2 hover:text-ink'}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>
        )}
        <span className="self-center mono text-xs text-ink-3 whitespace-nowrap">
          {rows.length} result{rows.length === 1 ? '' : 's'}
        </span>
        {/* Hands these filters to the screen that can group and export them, rather
            than growing a second copy of it here. `Filter & export` reads the same
            parameter names, so this is a translation of three and a pass-through of
            the rest. */}
        {can('reports') && (
          <Link className="btn btn-sm whitespace-nowrap" to={exportHref()}>
            Group &amp; export →
          </Link>
        )}
        {can('create') && (
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            Add task
          </button>
        )}
      </div>

      {/* The window, always visible and always changeable. */}
      <div className="filter-bar mb-4">
        {Object.entries(RANGES).map(([key, r]) => (
          <button key={key} type="button"
                  onClick={() => set({ range: key, from: null, to: null })}
                  className={`btn btn-sm ${range === key ? 'btn-primary' : ''}`}>
            {r.label}
          </button>
        ))}
        <span className="text-ink-3 text-[11.5px] px-1">or</span>
        <input type="date" className="field w-auto sm:w-40" value={from}
               onChange={(e) => set({ range: 'custom', from: e.target.value, to })} />
        <span className="text-ink-3 text-[11.5px]">to</span>
        <input type="date" className="field w-auto sm:w-40" value={to}
               onChange={(e) => set({ range: 'custom', from, to: e.target.value })} />
      </div>

      {/* Says what is being hidden, in both directions. Without this the page is
          quietly showing a slice and calling it everything. */}
      <p className="text-[11.8px] text-ink-3 mb-3">
        Showing <b className="text-ink-2">{inSentence}</b>
        {carriedOnly && statusRaw === 'OPEN,REWORK' && (
          <> · only work still <b className="text-ink-2">open or in rework</b> —{' '}
            <button className="underline hover:text-ink"
                    onClick={() => set({ status: '' })}>
              include finished ones
            </button>
          </>
        )}
        {carriedOnly && ' · these have all moved at least once'}
      </p>

      {tasks.isLoading ? <Spinner />
        : tasks.error ? <ErrorNote error={tasks.error} />
        : rows.length === 0 ? (
          <Empty
            title={windowed ? `Nothing in ${inSentence}`
                   : filtered ? 'No tasks match these filters'
                   : carriedOnly ? 'Nothing has been carried forward'
                   : 'No tasks yet'}
            hint={windowed
              ? 'There may be tasks outside this window. Widen it, or look at all time.'
              : filtered
                ? 'Try a different search, or clear the status filter.'
                : carriedOnly
                  ? 'Tasks appear here once they have moved to another working day at least once.'
                  : 'Tasks written for a day show up here.'}
            action={windowed
              ? <button className="btn btn-primary"
                        onClick={() => set({ range: 'all', from: null, to: null })}>
                  Show all time
                </button>
              : filtered
                ? <button className="btn"
                          onClick={() => set({ q: null, status: null,
                                               responsible: null, approver: null,
                                               company: null })}>
                    Clear filters
                  </button>
                : undefined} />
        ) : (
          <>
          <TaskList
            tasks={rows} onOpen={setOpen} groupByDate groupBy={groupBy}
            /* This screen reaches back a week by default and often a year, so
               every company heading opened expanded and the page ran to a few
               hundred rows. Folded, it opens as a list of clients (or people)
               with their counts and bars - and the one you want is a tap. */
            startFolded
            selection={dropping ? picked : undefined}
            footer={
              // Said only when it is true, because the headings really do add up to
              // more than the result count and a reader is entitled to notice.
              groupBy === 'person' && shared > 0
                ? `${shared} shared task(s) appear under each person.`
                : ''} />
          {dropping && <DropSelected selection={picked} channelName={channelName} />}
          </>
        )}

      {/* Re-derived from the refreshed list, not the click-time snapshot: an edit
          invalidates ['tasks'], and a drawer still showing the old text - and the old
          row_version - would send a stale expected_version on the next save and hit a
          conflict of its own making. Today.tsx does the same. */}
      {open && (
        <TaskDrawer
          task={rows.find((t) => t.task_id === open.task_id) ?? open}
          onClose={() => setOpen(null)}
        />
      )}
      {adding && <AddTaskDrawer onClose={() => setAdding(false)} />}
    </>
  )
}
